import { Schema, type DOMOutputSpec } from "prosemirror-model";
import { emojiUrl } from "@/emoji";
import type { FontFamily } from "@/types";

export type LineAttrs = {
  font: FontFamily;
  size: number;
  smallcaps: boolean;
  upper: boolean;
};

export const DEFAULT_LINE_ATTRS: LineAttrs = {
  font: "serif",
  size: 80,
  smallcaps: true,
  upper: false,
};

export function lineStyle(attrs: LineAttrs): string {
  return [
    `font-family: var(--label-font-${attrs.font === "mono" ? "mono" : "serif"})`,
    `font-size: ${attrs.size}px`,
    `font-variant-caps: ${attrs.smallcaps ? "all-small-caps" : "normal"}`,
    `font-variant-numeric: oldstyle-nums`,
    `text-transform: ${attrs.upper ? "uppercase" : "none"}`,
  ].join("; ");
}

export const schema = new Schema({
  nodes: {
    doc: { content: "line+" },

    line: {
      content: "inline*",
      defining: true,
      attrs: {
        font: { default: DEFAULT_LINE_ATTRS.font },
        size: { default: DEFAULT_LINE_ATTRS.size },
        smallcaps: { default: DEFAULT_LINE_ATTRS.smallcaps },
        upper: { default: DEFAULT_LINE_ATTRS.upper },
      },
      parseDOM: [
        {
          tag: "div.label-line",
          getAttrs: (dom) => {
            const el = dom as HTMLElement;
            return {
              font: (el.dataset.font as FontFamily) ?? DEFAULT_LINE_ATTRS.font,
              size: Number(el.dataset.size) || DEFAULT_LINE_ATTRS.size,
              smallcaps: el.dataset.smallcaps === "true",
              upper: el.dataset.upper === "true",
            };
          },
        },
      ],
      toDOM: (node): DOMOutputSpec => {
        const a = node.attrs as LineAttrs;
        return [
          "div",
          {
            class: "label-line",
            "data-font": a.font,
            "data-size": String(a.size),
            "data-smallcaps": String(a.smallcaps),
            "data-upper": String(a.upper),
            style: lineStyle(a),
          },
          0,
        ];
      },
    },

    text: { group: "inline" },

    emoji: {
      group: "inline",
      inline: true,
      atom: true,
      selectable: true,
      attrs: {
        slug: { default: "" },
        hex: { default: "" },
      },
      parseDOM: [
        {
          tag: "img[data-emoji]",
          getAttrs: (dom) => {
            const el = dom as HTMLElement;
            return {
              slug: el.getAttribute("data-emoji") ?? "",
              hex: el.getAttribute("data-hex") ?? "",
            };
          },
        },
      ],
      toDOM: (node): DOMOutputSpec => [
        "img",
        {
          class: "label-emoji",
          "data-emoji": node.attrs.slug,
          "data-hex": node.attrs.hex,
          src: emojiUrl(node.attrs.hex),
          alt: node.attrs.slug,
          draggable: "false",
        },
      ],
    },
  },
});
